import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  useBillingUsage,
  useSources,
  useDeleteSource,
  useReprocessSource,
  useSitemapChildren,
} from "../hooks/useApi";
import { api } from "../api/client";
import { useTranslation } from "../hooks/useTranslation";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { Skeleton } from "@/components/ui/skeleton";
import { useMinLoadingTime } from "@/hooks/useMinLoadingTime";
import SourcesFilters from "../components/sources/SourcesFilters";
import SourcesPagination from "../components/sources/SourcesPagination";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import TextSourceDialog from "../components/dialogs/TextSourceDialog";
import FaqSourceDialog from "../components/dialogs/FaqSourceDialog";
import ProductSourceDialog from "../components/dialogs/ProductSourceDialog";
import SourcesDataTable from "../components/SourcesDataTable";
import UrlSourceDialog from "../components/dialogs/UrlSourceDialog";
import UploadSourceDialog from "../components/dialogs/UploadSourceDialog";
import SitemapSourceDialog from "../components/dialogs/SitemapSourceDialog";
import SitemapDetailsDialog from "../components/dialogs/SitemapDetailsDialog";
import { useControlloLimite } from "../components/billing/AvvisoLimitePiano";
import { BrandCard, BrandStyles, VARIABILI_SEZIONE } from "../components/integrations/brand";
import { BOTTONE, GRIGIO, SCURO, IntestazioneCasella } from "../components/integrations/casella";
import {
  Plus,
  Database,
  FileText,
  Link as LinkIcon,
  Upload,
  Trash2,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Clock,
  XCircle,
  ExternalLink,
  Map,
  ChevronDown,
  ChevronRight,
  ShoppingCart,
  Info,
  Package,
  Star,
  Eye,
  Sparkles,
  Pencil,
  HelpCircle,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const SourcesTab = ({ botId, bot, showHeader = true }) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const location = useLocation();

  // Server-side pagination + filter state
  // Fonti per pagina: 20, 50 o 100 (l'API ne accetta al massimo 100).
  const PAGE_SIZES = [20, 50, 100];
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [currentPage, setCurrentPage] = useState(1); // 1-based (server)
  const [search, setSearch] = useState(""); // applied search (the table debounces input)
  const [typeTab, setTypeTab] = useState("all");
  const [statusFilter, setStatusFilter] = useState(""); // "" = all; ready|processing|pending|error

  // API calls with TRUE server-side pagination
  const {
    data: sourcesResponse,
    isLoading,
    error,
    refetch,
  } = useSources(botId, {
    page: currentPage,
    limit: pageSize,
    search,
    type: typeTab === "all" ? "" : typeTab,
    status: statusFilter,
  });

  const showSkeleton = useMinLoadingTime(isLoading);

  const deleteSourceMutation = useDeleteSource();
  const reprocessSourceMutation = useReprocessSource();

  const [sourceToDelete, setSourceToDelete] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [processingStatuses, setProcessingStatuses] = useState({});

  // Multi-select for bulk delete — TanStack row selection state ({ [sourceId]: true }).
  const [rowSelection, setRowSelection] = useState({});
  const [isBulkDeleteOpen, setIsBulkDeleteOpen] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  // Rielabora in blocco. allMatching: selezionate tutte le fonti del filtro,
  // non solo quelle della pagina (es. le 700 in errore).
  const [isBulkReprocessOpen, setIsBulkReprocessOpen] = useState(false);
  const [isBulkReprocessing, setIsBulkReprocessing] = useState(false);
  const [allMatching, setAllMatching] = useState(false);

  // Dialog states
  const [isTextDialogOpen, setIsTextDialogOpen] = useState(false);
  const [editTextSource, setEditTextSource] = useState(null);
  const [editFaqSource, setEditFaqSource] = useState(null);
  const [isFaqDialogOpen, setIsFaqDialogOpen] = useState(false);
  const [isProductDialogOpen, setIsProductDialogOpen] = useState(false);
  const [editProductSource, setEditProductSource] = useState(null);
  const [isUrlDialogOpen, setIsUrlDialogOpen] = useState(false);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [isSitemapDialogOpen, setIsSitemapDialogOpen] = useState(false);
  const [productDetailsModal, setProductDetailsModal] = useState(null);
  const [sitemapDetailsModal, setSitemapDetailsModal] = useState(null);

  // Handle suggested sources from wizard
  const [suggestedSources, setSuggestedSources] = useState(null);
  const [showSuggestedPrompt, setShowSuggestedPrompt] = useState(false);

  useEffect(() => {
    // Check if we have suggested sources from onboarding wizard
    if (location.state?.suggestedSources) {
      setSuggestedSources(location.state.suggestedSources);
      setShowSuggestedPrompt(true);

      // Clear the location state to avoid re-showing on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const handleAddSuggestedSource = (source) => {
    seCiSta(() => (source.type === "sitemap" ? setIsSitemapDialogOpen(true) : setIsUrlDialogOpen(true)))();
    // Pre-fill the dialog would require modifications to the dialog components
  };

  // Extract sources and pagination from response
  const sources = sourcesResponse?.sources || [];
  const pagination = sourcesResponse?.pagination || null;
  const counts = sourcesResponse?.counts || {};

  // Le fonti del piano (di tutta l'organizzazione, non solo di questo
  // assistente): quante se ne usano e quante ne comprende (0 = illimitate).
  // Si rilegge quando cambia il numero di fonti.
  const consumi = useBillingUsage();
  const maxFonti = consumi.data?.effective?.limits?.sources_max || 0;
  const fontiUsate = consumi.data?.usage?.sources_count || 0;
  const pianoPieno = maxFonti > 0 && fontiUsate >= maxFonti;
  // A piano pieno chi puo' comprare non apre la finestra della fonte, che
  // finirebbe nell'errore del limite: vede subito i piani (FinestraPiani in
  // AvvisoLimitePiano). Contano tutte le fonti aggiunte a mano. Chi non puo'
  // comprare (collaboratori, venduti offline) resta come prima.
  const { controlla } = useControlloLimite();
  const seCiSta = (apri) => controlla({ limite: "sources_max" }, apri);
  const { refetch: rileggiConsumi } = consumi;
  useEffect(() => {
    rileggiConsumi();
  }, [counts.all, rileggiConsumi]);
  const totalItems = pagination?.total_items ?? 0;
  const totalPages = pagination?.total_pages ?? 1;
  // Filtering active? (used to distinguish "no results" from "bot has no sources").
  const isFiltering = Boolean(search) || typeTab !== "all" || Boolean(statusFilter);
  const botIsEmpty = !isFiltering && currentPage === 1 && totalItems === 0;

  // Selected sources on the CURRENT page (server pagination → per-page selection):
  // derived from rows present, so a deleted row drops out of the count.
  const selectedSources = sources.filter((s) => rowSelection[s._id]);
  const pageSelectedCount = selectedSources.length;
  const wholePageSelected = sources.length > 0 && pageSelectedCount === sources.length;
  // "Tutte del filtro" vale solo finche' la pagina resta tutta selezionata.
  const selectingAllMatching = allMatching && wholePageSelected;
  const selectedCount = selectingAllMatching ? totalItems : pageSelectedCount;

  // Filter and pagination handlers
  // Any filter change resets to page 1 (so you never land on an empty page).
  const handleSearchChange = (value) => {
    setSearch(value);
    setCurrentPage(1);
  };
  const handleTabChange = (value) => {
    setTypeTab(value);
    setCurrentPage(1);
  };
  const handleStatusChange = (value) => {
    setStatusFilter(value === "all" ? "" : value);
    setCurrentPage(1);
  };
  const handlePageChange = (zeroBasedIndex) => {
    setCurrentPage(zeroBasedIndex + 1); // table is 0-based, server is 1-based
  };
  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  // Server pagination → selection is per-page; clear it whenever the visible set
  // changes so stale ids can't leak into a later bulk action.
  useEffect(() => {
    setRowSelection({});
    setAllMatching(false);
  }, [currentPage, pageSize, typeTab, search, statusFilter]);

  // Poll for status updates every 5 seconds for processing sources
  useEffect(() => {
    const hasProcessingSources =
      Array.isArray(sources) &&
      sources.some(
        (source) =>
          source.status === "processing" || source.status === "pending",
      );

    if (!hasProcessingSources) return;

    const interval = setInterval(() => {
      refetch();
    }, 5000);

    return () => clearInterval(interval);
  }, [sources, refetch]);

  const formatLastRun = (timestamp) => {
    if (!timestamp) return t("sources.never", "Never");
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMinutes = Math.floor(diffMs / (1000 * 60));

    if (diffMinutes < 1) return t("sources.justNow", "Just now");
    if (diffMinutes < 60)
      return t("sources.minutesAgo", "{{count}}m ago", { count: diffMinutes });

    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24)
      return t("sources.hoursAgo", "{{count}}h ago", { count: diffHours });

    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7)
      return t("sources.daysAgo", "{{count}}d ago", { count: diffDays });

    return date.toLocaleDateString();
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "ready":
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case "processing":
        return <RefreshCw className="h-4 w-4 text-blue-600 animate-spin" />;
      case "pending":
        return <Clock className="h-4 w-4 text-yellow-600" />;
      case "error":
        return <XCircle className="h-4 w-4 text-red-600" />;
      default:
        return <Clock className="h-4 w-4 text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "ready":
        return "bg-green-100 text-green-800";
      case "processing":
        return "bg-blue-100 text-blue-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "error":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Stesso grigio per tutti i tipi: il tipo lo dice l'icona, il colore
  // resta per lo stato (pronto, in corso, errore).
  const getTypeIcon = (source) => {
    // Se è un oggetto, estrae il type, altrimenti assume che sia già il type (per retrocompatibilità)
    const type = typeof source === "object" ? source.type : source;
    const parentSitemapId =
      typeof source === "object" ? source.parent_sitemap_id : null;
    const cls = "h-4 w-4 text-[#475569]";

    switch (type) {
      case "pdf":
        return <FileText className={cls} />;
      case "url":
        // Se l'URL proviene da una sitemap, usa l'icona della sitemap
        if (parentSitemapId) {
          return <Map className={cls} />;
        }
        return <LinkIcon className={cls} />;
      case "faq":
        return <HelpCircle className={cls} />;
      case "product":
        return <ShoppingCart className={cls} />;
      case "sitemap":
        return <Map className={cls} />;
      default:
        return <FileText className={cls} />;
    }
  };

  const handleDeleteSource = async () => {
    if (!sourceToDelete) return;

    try {
      await deleteSourceMutation.mutateAsync({
        botId,
        sourceId: sourceToDelete._id,
      });
      // Drop the deleted row from the selection too (keep rowSelection tidy).
      setRowSelection((prev) => {
        if (!prev[sourceToDelete._id]) return prev;
        const next = { ...prev };
        delete next[sourceToDelete._id];
        return next;
      });
      setIsDeleteDialogOpen(false);
      setSourceToDelete(null);
    } catch (error) {
      console.error("Failed to delete source:", error);
    }
  };

  // Rielabora le fonti scelte, o tutte quelle del filtro: una richiesta, la
  // coda riceve i lavori a pacchi (bulk-reingest).
  const handleBulkReprocess = async () => {
    const body = selectingAllMatching
      ? { all: true, filter: { type: typeTab === "all" ? "" : typeTab, status: statusFilter, search } }
      : { sourceIds: selectedSources.map((s) => s._id) };
    setIsBulkReprocessing(true);
    try {
      const esito = await api.sources.bulkReingest(botId, body);
      toast({
        variant: "success",
        title: t("sources.bulkReprocessQueued", "{{count}} sources are being reprocessed", { count: esito.queued }),
        description: esito.skippedProcessing
          ? t("sources.bulkReprocessSkipped", "{{count}} were already processing and were skipped.", { count: esito.skippedProcessing })
          : undefined,
      });
      setRowSelection({});
      setAllMatching(false);
      setIsBulkReprocessOpen(false);
    } catch (error) {
      console.error("Failed to bulk reprocess sources:", error);
      const queued = error?.data?.queued;
      toast({
        variant: "destructive",
        title: queued
          ? t("sources.bulkReprocessPartial", "Only {{count}} sources were queued: try again for the others", { count: queued })
          : t("sources.reprocessError", "Failed to reprocess source"),
      });
    } finally {
      setIsBulkReprocessing(false);
      refetch();
    }
  };

  // Single backend request that deletes all selected sources (with full cleanup).
  const handleBulkDelete = async () => {
    const ids = selectedSources.map((s) => s._id);
    if (ids.length === 0) return;
    setIsBulkDeleting(true);
    try {
      await api.sources.bulkDelete(botId, ids);
      toast({
        variant: "success",
        title: t("sources.bulkDeleted", "Selected sources deleted"),
      });
      setRowSelection({});
      setIsBulkDeleteOpen(false);
    } catch (error) {
      console.error("Failed to bulk delete sources:", error);
      toast({ variant: "destructive", title: t("common.error", "Error") });
    } finally {
      setIsBulkDeleting(false);
      // If we cleared the whole current page and it's not page 1, step back so
      // the user isn't stranded on an empty page; otherwise just refetch.
      if (ids.length >= sources.length && currentPage > 1) {
        setCurrentPage((p) => Math.max(1, p - 1));
      } else {
        refetch();
      }
    }
  };

  const handleReprocessSource = async (sourceId) => {
    try {
      setProcessingStatuses((prev) => ({ ...prev, [sourceId]: true }));
      await reprocessSourceMutation.mutateAsync({
        botId,
        sourceId,
      });
      // No success toast: the status badge (In attesa → In corso → Pronto) is the
      // feedback now. The error toast below stays so failures aren't silent.
    } catch (error) {
      console.error("Failed to reprocess source:", error);
      toast({
        variant: "destructive",
        title: t("common.error", "Errore"),
        description: t(
          "sources.reprocessError",
          "Impossibile avviare il riprocessamento. Riprova."
        ),
      });
    } finally {
      setProcessingStatuses((prev) => ({ ...prev, [sourceId]: false }));
    }
  };

  const openDeleteDialog = (source) => {
    setSourceToDelete(source);
    setIsDeleteDialogOpen(true);
  };

  // Handle successful source creation
  const handleSourceSuccess = () => {
    refetch(); // Refresh the sources list
  };

  const getSourceDisplayName = (source) => {
    // For URL sources, prioritize metadata.title if available
    if (source.type === "url") {
      return source.metadata?.title || source.url;
    }
    if (source.type === "sitemap") return source.url;
    if (source.type === "pdf" && source.filename) return source.filename;
    if (source.type === "text")
      return `${
        source.title || t("sources.textDocument", "Text Document")
      } (${source.inline_text?.substring(0, 50)}...)`;
    if (source.type === "faq") return source.question || "FAQ";
    if (source.type === "product") return source.product_name || "Prodotto";
    return t("sources.genericSource", "{{type}} Source", {
      type: source.type.toUpperCase(),
    });
  };

  const getSourceSubtitle = (source) => {
    const fmtSourceDate = (d) => {
      if (!d) return "";
      const dt = new Date(d);
      return isNaN(dt.getTime())
        ? ""
        : dt.toLocaleDateString(undefined, {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          });
    };
    if (source.type === "sitemap") {
      const refreshText = source.refresh_days
        ? t("sources.refreshesEveryDays", "Refreshes every {{count}} days", {
            count: source.refresh_days,
          })
        : "";
      const childText = source.childUrlCount
        ? t("sources.urlsFound", "{{count}} URLs found", {
            count: source.childUrlCount,
          })
        : t("sources.sitemap", "Sitemap");
      return refreshText ? `${childText} • ${refreshText}` : childText;
    }
    if (source.type === "url" && source.parent_sitemap_id) {
      const refreshText = source.refresh_days
        ? t("sources.refreshesEveryDays", "Refreshes every {{count}} days", {
            count: source.refresh_days,
          })
        : "";
      const parentText = t("sources.fromSitemap", "From sitemap");
      return refreshText ? `${parentText} • ${refreshText}` : parentText;
    }
    if (source.type === "url" && source.refresh_days) {
      return t("sources.refreshesEveryDays", "Refreshes every {{count}} days", {
        count: source.refresh_days,
      });
    }
    // Other sources: show ORIGIN (manual vs page analysis) + when it was added,
    // not the type (already shown in the Type column).
    const created = fmtSourceDate(source.createdAt);
    const manual = t("sources.manual", "Manuale");
    const fromCrawl = t("sources.fromPageAnalysis", "Da analisi pagina");
    if (source.type === "faq") {
      const origin = source.faq_origin === "crawl" ? fromCrawl : manual;
      return created ? `${origin} • ${created}` : origin;
    }
    if (source.type === "product") {
      const origin = source.product_origin === "crawl" ? fromCrawl : manual;
      return created ? `${origin} • ${created}` : origin;
    }
    if (source.type === "text") {
      return created ? `${manual} • ${created}` : manual;
    }
    if (source.type === "pdf") {
      const uploaded = t("sources.uploaded", "Caricato");
      return created ? `${uploaded} • ${created}` : uploaded;
    }
    return source.type.toUpperCase();
  };

  const handleViewSitemap = (source) => {
    setSitemapDetailsModal(source);
  };

  // Open an uploaded document (pdf) in a new tab using a short-lived signed URL.
  const handleViewDocument = async (source) => {
    // Open the tab synchronously (before the async call) to avoid popup blockers.
    const win = window.open("", "_blank");
    try {
      const { url } = await api.sources.getViewUrl(source._id);
      if (win) {
        win.opener = null; // sever the opener relationship for safety
        win.location = url;
      } else {
        window.open(url, "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      console.error("Failed to open document:", err);
      if (win) win.close();
      alert(
        t(
          "sources.openDocumentError",
          "Impossibile aprire il documento. Riprova.",
        ),
      );
    }
  };

  const handleDeleteSitemapUrls = async (sitemapId) => {
    if (!sitemapId) return;

    try {
      // We'll need to call the API to delete all URLs from this sitemap
      await deleteSourceMutation.mutateAsync({
        botId,
        sourceId: sitemapId,
      });
      setSitemapDetailsModal(null);
      refetch();
    } catch (error) {
      console.error("Failed to delete sitemap URLs:", error);
    }
  };

  // I modi di aggiungere una fonte: nel menu "Aggiungi fonte" e, a elenco
  // vuoto, come tessere da scegliere.
  const tipiFonte = [
    {
      chiave: "url",
      Icona: LinkIcon,
      etichetta: t("sources.addUrl", "Add URL"),
      descrizione: t("sources.empty.url", "Una pagina del tuo sito"),
      apri: seCiSta(() => setIsUrlDialogOpen(true)),
    },
    {
      chiave: "sitemap",
      Icona: Map,
      etichetta: t("sources.addSitemap", "Add Sitemap"),
      descrizione: t("sources.empty.sitemap", "Tutte le pagine del sito, aggiornate da sole"),
      apri: seCiSta(() => setIsSitemapDialogOpen(true)),
    },
    {
      chiave: "upload",
      Icona: Upload,
      etichetta: t("sources.uploadFile", "Upload File"),
      descrizione: t("sources.empty.upload", "PDF e documenti"),
      apri: seCiSta(() => setIsUploadDialogOpen(true)),
    },
    {
      chiave: "text",
      Icona: FileText,
      etichetta: t("sources.addText", "Add Text"),
      descrizione: t("sources.empty.text", "Informazioni scritte da te"),
      apri: seCiSta(() => setIsTextDialogOpen(true)),
    },
    {
      chiave: "faq",
      Icona: HelpCircle,
      etichetta: t("sources.addFaq", "Aggiungi FAQ"),
      descrizione: t("sources.empty.faq", "Domande e risposte pronte"),
      apri: seCiSta(() => setIsFaqDialogOpen(true)),
    },
    {
      chiave: "product",
      Icona: ShoppingCart,
      etichetta: t("sources.addProduct", "Aggiungi prodotto"),
      descrizione: t("sources.empty.product", "Una scheda prodotto"),
      apri: seCiSta(() => setIsProductDialogOpen(true)),
    },
  ];

  const menuAggiungi = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className={SCURO}>
          <Plus className="h-3.5 w-3.5" aria-hidden="true" />
          {t("sources.addSource", "Add source")}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52 rounded-xl">
        {tipiFonte.map(({ chiave, Icona, etichetta, apri }) => (
          <DropdownMenuItem key={chiave} onClick={apri} className="rounded-lg">
            <Icona className="mr-2 h-4 w-4 text-[#475569]" /> {etichetta}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  // Quante fonti in tutto (i conteggi seguono il filtro per stato). A zero
  // non serve: lo dice gia' la pagina vuota.
  const totaleFonti = !statusFilter && counts.all > 0 ? counts.all : null;

  const Avviso = ({ titolo, testo }) => (
    <div className="flex gap-3 rounded-[14px] border border-red-200 bg-red-50 p-4">
      <AlertTriangle className="h-4 w-4 shrink-0 text-red-500" aria-hidden="true" />
      <div>
        <h3 className="text-sm font-medium text-red-800">{titolo}</h3>
        <p className="mt-1 text-sm text-red-700">{testo}</p>
      </div>
    </div>
  );

  return (
    <div className="relative isolate" style={VARIABILI_SEZIONE}>
      <BrandStyles />

      {showHeader && (
        <IntestazioneCasella
          icona={Database}
          titolo={t("sources.title")}
          dettagli={
            <span className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-sm text-[#475569]">
              {totaleFonti !== null ? (
                <span>
                  <strong className="font-semibold text-[#020617]">{totaleFonti}</strong>{" "}
                  {t("sources.resultsLabel", "fonti")}
                </span>
              ) : null}
              {maxFonti > 0 ? (
                <span className={pianoPieno ? "font-medium text-[#B45309]" : ""}>
                  {t("sources.planUsage", { used: fontiUsate, max: maxFonti })}
                  {pianoPieno ? (
                    <>
                      {" · "}
                      <Link to="/billing" className="font-semibold underline underline-offset-2">
                        {t("billing.limitSeePlans")}
                      </Link>
                    </>
                  ) : null}
                </span>
              ) : null}
              <span className="line-clamp-1 max-w-xl text-[#64748B]">{t("sources.subtitle")}</span>
            </span>
          }
          azioni={
            <>
              {selectedCount > 0 && (
                <>
                  <span className="hidden self-center text-xs text-[#64748B] sm:inline">
                    {selectedCount} {t("sources.selectedLabel", "selezionate")}
                  </span>
                  <button type="button" className={GRIGIO} onClick={() => setIsBulkReprocessOpen(true)}>
                    <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
                    {t("sources.reprocessSelected", "Reprocess selected")}
                  </button>
                  {/* Eliminare "tutte del filtro" no: si elimina solo cio' che si vede. */}
                  {!selectingAllMatching && (
                    <button
                      type="button"
                      className={`${BOTTONE} text-[#B91C1C] hover:bg-red-50`}
                      onClick={() => setIsBulkDeleteOpen(true)}
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                      {t("sources.deleteSelected", "Elimina selezionati")}
                    </button>
                  )}
                </>
              )}
              {menuAggiungi}
            </>
          }
        />
      )}

      <div className="pu-fade-up mx-auto max-w-[1400px] space-y-4 pb-10">
        {/* Fonti suggerite dall'onboarding */}
        {showSuggestedPrompt && suggestedSources && suggestedSources.length > 0 && (
          <BrandCard className="p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="flex items-center gap-2 text-sm font-semibold text-[#020617]">
                  <Sparkles className="h-4 w-4 text-[#A855F7]" aria-hidden="true" />
                  {t("sources.suggestedSources")}
                </h2>
                <p className="mt-1 text-xs text-[#64748B]">{t("sources.suggestedDesc")}</p>
              </div>
              <button
                type="button"
                className={`${BOTTONE} text-[#64748B] hover:bg-[#F1F5F9]`}
                onClick={() => setShowSuggestedPrompt(false)}
              >
                {t("sources.dismiss")}
              </button>
            </div>
            <ul className="mt-4 space-y-2">
              {suggestedSources.map((source, index) => (
                <li
                  key={index}
                  className="flex items-center justify-between gap-3 rounded-[10px] border border-[#E2E8F0] p-3"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#F1F5F9]">
                      {source.type === "sitemap" ? (
                        <Map className="h-4 w-4 text-[#475569]" />
                      ) : (
                        <LinkIcon className="h-4 w-4 text-[#475569]" />
                      )}
                    </span>
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-[#020617]">{source.name}</div>
                      <div className="truncate text-xs text-[#64748B]">{source.url}</div>
                    </div>
                  </div>
                  <button type="button" className={GRIGIO} onClick={() => handleAddSuggestedSource(source)}>
                    <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                    {t("sources.add")}
                  </button>
                </li>
              ))}
            </ul>
          </BrandCard>
        )}

        {deleteSourceMutation.error && (
          <Avviso
            titolo={t("sources.failedDelete", "Failed to delete source")}
            testo={deleteSourceMutation.error.message}
          />
        )}
        {reprocessSourceMutation.error && (
          <Avviso
            titolo={t("sources.failedReprocess", "Failed to reprocess source")}
            testo={reprocessSourceMutation.error.message}
          />
        )}

        {/* Senza intestazione il menu per aggiungere sta sopra l'elenco. */}
        {!showHeader && <div className="flex justify-end">{menuAggiungi}</div>}

        {showSkeleton && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Skeleton className="h-10 w-80 rounded-[12px]" />
              <Skeleton className="h-10 w-64 rounded-lg" />
            </div>
            <div className="overflow-hidden rounded-[14px] border border-[#E2E8F0] bg-white">
              <div className="flex items-center gap-4 border-b border-[#E2E8F0] bg-[#F8FAFC] px-4 py-3">
                <Skeleton className="h-4 w-4" />
                <Skeleton className="h-4 w-12" />
                <Skeleton className="h-4 w-40" />
                <Skeleton className="ml-auto h-4 w-16" />
              </div>
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4 border-b border-[#F1F5F9] px-4 py-4 last:border-0">
                  <Skeleton className="h-4 w-4 flex-shrink-0" />
                  <Skeleton className="h-8 w-8 flex-shrink-0 rounded-lg" />
                  <Skeleton className="h-4 w-1/3" />
                  <Skeleton className="h-5 w-20 rounded-full" />
                  <Skeleton className="hidden h-4 w-24 sm:block" />
                  <Skeleton className="ml-auto h-8 w-8 rounded-[8px]" />
                </div>
              ))}
            </div>
          </div>
        )}

        {!showSkeleton && error && (
          <Avviso titolo={t("sources.errorLoading", "Error loading sources")} testo={error.message} />
        )}

        {/* Pagina tutta selezionata e altre fonti nelle pagine dopo: si puo'
            estendere la selezione a tutte quelle del filtro (come Gmail). */}
        {!showSkeleton && showHeader && wholePageSelected && totalItems > sources.length && (
          <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 rounded-[12px] border border-[#E2E8F0] bg-[#F8FAFC] px-4 py-2.5 text-sm text-[#334155]">
            {selectingAllMatching ? (
              <>
                <span>
                  {t("sources.allMatchingSelected", "All {{count}} sources of this filter are selected.", { count: totalItems })}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setAllMatching(false);
                    setRowSelection({});
                  }}
                  className="font-semibold text-[#18181B] underline underline-offset-2"
                >
                  {t("sources.clearSelection", "Clear selection")}
                </button>
              </>
            ) : (
              <>
                <span>
                  {t("sources.pageSelected", "The {{count}} sources on this page are selected.", { count: sources.length })}
                </span>
                <button
                  type="button"
                  onClick={() => setAllMatching(true)}
                  className="font-semibold text-[#18181B] underline underline-offset-2"
                >
                  {t("sources.selectAllMatching", "Select all {{count}}", { count: totalItems })}
                </button>
              </>
            )}
          </div>
        )}

        {!showSkeleton && !botIsEmpty && (
          <SourcesDataTable
            sources={sources}
            pageIndex={currentPage - 1}
            pageSize={pageSize}
            pageSizes={PAGE_SIZES}
            onPageSizeChange={handlePageSizeChange}
            pageCount={totalPages}
            totalCount={totalItems}
            onPageChange={handlePageChange}
            search={search}
            onSearchChange={handleSearchChange}
            tab={typeTab}
            onTabChange={handleTabChange}
            status={statusFilter}
            onStatusChange={handleStatusChange}
            counts={counts}
            isLoading={isLoading}
            getTypeIcon={getTypeIcon}
            getSourceDisplayName={getSourceDisplayName}
            getSourceSubtitle={getSourceSubtitle}
            onReprocess={handleReprocessSource}
            onDelete={openDeleteDialog}
            onEditText={setEditTextSource}
            onEditFaq={setEditFaqSource}
            onEditProduct={setEditProductSource}
            reprocessing={reprocessSourceMutation.isPending}
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
            t={t}
          />
        )}

        {/* Nessuna fonte ancora: i modi di aggiungerne una, da scegliere. */}
        {!showSkeleton && botIsEmpty && (
          <BrandCard className="p-6 sm:p-8">
            <div className="max-w-xl space-y-2">
              <h2 className="text-lg font-semibold text-[#020617]">
                {t("sources.noSourcesYet", "No sources added yet")}
              </h2>
              <p className="text-sm leading-6 text-[#475569]">
                {t("sources.noSourcesDesc", "Add documents, URLs, or text content to build your knowledge base.")}
              </p>
            </div>
            <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {tipiFonte.map(({ chiave, Icona, etichetta, descrizione, apri }) => (
                <button
                  key={chiave}
                  type="button"
                  onClick={apri}
                  className="flex items-start gap-3 rounded-[14px] border border-[#E2E8F0] p-4 text-left transition-colors hover:border-[#CBD5E1] hover:bg-[#F8FAFC] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#F1F5F9]">
                    <Icona className="h-4 w-4 text-[#334155]" aria-hidden="true" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-[#020617]">{etichetta}</span>
                    <span className="mt-0.5 block text-xs text-[#64748B]">{descrizione}</span>
                  </span>
                </button>
              ))}
            </div>
          </BrandCard>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="rounded-[14px] sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>{t("sources.deleteSource", "Delete Source")}</DialogTitle>
            <DialogDescription>
              {t(
                "sources.deleteSourceConfirm",
                "Are you sure you want to delete this source? This action cannot be undone.",
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="rounded-[10px] border border-[#E2E8F0] bg-[#F8FAFC] p-4">
              <div className="flex">
                <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />
                <div className="ml-3">
                  <h4 className="text-sm font-medium text-[#020617]">
                    {t("sources.warning", "Warning")}
                  </h4>
                  <div className="mt-1 text-sm text-[#475569]">
                    <p>
                      {t(
                        "sources.willPermanentlyDelete",
                        "This will permanently delete:",
                      )}
                    </p>
                    <ul className="list-disc list-inside mt-2 space-y-1">
                      <li>
                        {t(
                          "sources.deleteItemConfig",
                          "The source configuration",
                        )}
                      </li>
                      <li>
                        {t(
                          "sources.deleteItemChunks",
                          "All associated content chunks",
                        )}
                      </li>
                      <li>
                        {t(
                          "sources.deleteItemEmbeddings",
                          "All vector embeddings for this source",
                        )}
                      </li>
                      {sourceToDelete?.type === "sitemap" &&
                        sourceToDelete?.childUrlCount > 0 && (
                          <li className="font-medium text-red-700">
                            {t(
                              "sources.deleteItemSitemapUrls",
                              "All {{count}} related URL sources from this sitemap",
                              { count: sourceToDelete.childUrlCount },
                            )}
                          </li>
                        )}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            {sourceToDelete && (
              <div className="mt-3 rounded-[10px] border border-[#E2E8F0] p-3">
                <div className="flex items-center space-x-2">
                  {getTypeIcon(sourceToDelete)}
                  <span className="text-sm font-medium">
                    {getSourceDisplayName(sourceToDelete)}
                  </span>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <button type="button" className={GRIGIO} onClick={() => setIsDeleteDialogOpen(false)}>
              {t("common.cancel")}
            </button>
            <button
              type="button"
              className={`${BOTTONE} bg-[#DC2626] text-white hover:bg-[#B91C1C]`}
              onClick={handleDeleteSource}
              disabled={deleteSourceMutation.isPending}
            >
              {deleteSourceMutation.isPending ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  {t("sources.deleting", "Deleting...")}
                </>
              ) : (
                <>
                  <Trash2 className="h-3.5 w-3.5" />
                  {t("sources.deleteSource", "Delete Source")}
                </>
              )}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk reprocess confirmation */}
      <Dialog open={isBulkReprocessOpen} onOpenChange={setIsBulkReprocessOpen}>
        <DialogContent className="rounded-[14px] sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>
              {t("sources.bulkReprocessTitle", "Reprocess {{count}} sources?", { count: selectedCount })}
            </DialogTitle>
            <DialogDescription>
              {t(
                "sources.bulkReprocessConfirm",
                "They are read and indexed again. With many sources it takes a few minutes; the status updates by itself. Sources already processing are skipped."
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button type="button" className={GRIGIO} onClick={() => setIsBulkReprocessOpen(false)} disabled={isBulkReprocessing}>
              {t("common.cancel", "Cancel")}
            </button>
            <button type="button" className={SCURO} onClick={handleBulkReprocess} disabled={isBulkReprocessing}>
              <RefreshCw className={`h-3.5 w-3.5 ${isBulkReprocessing ? "animate-spin" : ""}`} aria-hidden="true" />
              {t("sources.reprocessSelected", "Reprocess selected")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk delete confirmation */}
      <Dialog open={isBulkDeleteOpen} onOpenChange={setIsBulkDeleteOpen}>
        <DialogContent className="rounded-[14px] sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>{t("sources.bulkDeleteTitle", "Delete selected sources")}</DialogTitle>
            <DialogDescription>
              {t(
                "sources.bulkDeleteConfirm",
                "You are about to delete {{count}} sources. This action cannot be undone.",
                { count: selectedCount }
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button type="button" className={GRIGIO} onClick={() => setIsBulkDeleteOpen(false)} disabled={isBulkDeleting}>
              {t("common.cancel", "Cancel")}
            </button>
            <button
              type="button"
              className={`${BOTTONE} bg-[#DC2626] text-white hover:bg-[#B91C1C]`}
              onClick={handleBulkDelete}
              disabled={isBulkDeleting}
            >
              <Trash2 className="h-3.5 w-3.5" />
              {t("sources.deleteSelected", "Delete selected")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Source Creation Dialogs */}
      <TextSourceDialog
        isOpen={isTextDialogOpen}
        onOpenChange={setIsTextDialogOpen}
        botId={botId}
        onSuccess={handleSourceSuccess}
      />

      {/* FAQ source create dialog */}
      <FaqSourceDialog
        isOpen={isFaqDialogOpen}
        onOpenChange={setIsFaqDialogOpen}
        botId={botId}
        onSuccess={handleSourceSuccess}
      />

      {/* FAQ source edit dialog (manual FAQ only) */}
      <FaqSourceDialog
        isOpen={Boolean(editFaqSource)}
        onOpenChange={(open) => {
          if (!open) setEditFaqSource(null);
        }}
        botId={botId}
        editSource={editFaqSource}
        onSuccess={() => {
          handleSourceSuccess();
          toast({
            variant: "success",
            title: t("sources.faqUpdated", "FAQ aggiornata"),
            description: t(
              "sources.faqUpdatedDesc",
              "Le modifiche vengono re-indicizzate."
            ),
          });
        }}
      />

      {/* Product source create dialog */}
      <ProductSourceDialog
        isOpen={isProductDialogOpen}
        onOpenChange={setIsProductDialogOpen}
        botId={botId}
        onSuccess={handleSourceSuccess}
      />

      {/* Product source edit dialog (manual products only) */}
      <ProductSourceDialog
        isOpen={Boolean(editProductSource)}
        onOpenChange={(open) => {
          if (!open) setEditProductSource(null);
        }}
        botId={botId}
        editSource={editProductSource}
        onSuccess={() => {
          handleSourceSuccess();
          toast({
            variant: "success",
            title: t("sources.productUpdated", "Prodotto aggiornato"),
            description: t(
              "sources.productUpdatedDesc",
              "Le modifiche vengono re-indicizzate."
            ),
          });
        }}
      />

      {/* Text source edit dialog */}
      <TextSourceDialog
        isOpen={Boolean(editTextSource)}
        onOpenChange={(open) => {
          if (!open) setEditTextSource(null);
        }}
        botId={botId}
        editSource={editTextSource}
        onSuccess={() => {
          handleSourceSuccess();
          toast({
            variant: "success",
            title: t("sources.textUpdated", "Text source updated"),
            description: t(
              "sources.textUpdatedDesc",
              "Your changes are being re-indexed."
            ),
          });
        }}
      />

      <UrlSourceDialog
        isOpen={isUrlDialogOpen}
        onOpenChange={setIsUrlDialogOpen}
        botId={botId}
        onSuccess={handleSourceSuccess}
      />

      <UploadSourceDialog
        isOpen={isUploadDialogOpen}
        onOpenChange={setIsUploadDialogOpen}
        botId={botId}
        onSuccess={handleSourceSuccess}
      />

      <SitemapSourceDialog
        isOpen={isSitemapDialogOpen}
        onClose={() => setIsSitemapDialogOpen(false)}
        botId={botId}
        onSuccess={handleSourceSuccess}
      />

      {/* Product Details Modal */}
      <Dialog
        open={productDetailsModal !== null}
        onOpenChange={() => setProductDetailsModal(null)}
      >
        <DialogContent className="max-h-[80vh] max-w-2xl overflow-y-auto rounded-[14px]">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Package className="h-5 w-5 text-[#475569]" />
              <span>{t("sources.productDetails", "Product Details")}</span>
            </DialogTitle>
            <DialogDescription>
              {t(
                "sources.productDetailsDesc",
                "Structured product information extracted from the URL",
              )}
            </DialogDescription>
          </DialogHeader>

          {productDetailsModal?.product_metadata && (
            <div className="space-y-6">
              {/* Basic Product Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {productDetailsModal.product_metadata.image_url && (
                  <div className="md:col-span-2">
                    <img
                      src={productDetailsModal.product_metadata.image_url}
                      alt={
                        productDetailsModal.product_metadata.name ||
                        t("sources.product", "Product")
                      }
                      className="w-full max-w-sm mx-auto rounded-lg border"
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    {t("sources.productName", "Name")}
                  </label>
                  <p className="text-sm mt-1">
                    {productDetailsModal.product_metadata.name ||
                      t("sources.notAvailable", "N/A")}
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    {t("sources.productBrand", "Brand")}
                  </label>
                  <p className="text-sm mt-1">
                    {productDetailsModal.product_metadata.brand ||
                      t("sources.notAvailable", "N/A")}
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    {t("sources.productSku", "SKU")}
                  </label>
                  <p className="text-xs mt-1 font-mono">
                    {productDetailsModal.product_metadata.sku ||
                      t("sources.notAvailable", "N/A")}
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    {t("sources.productCategory", "Category")}
                  </label>
                  <p className="text-sm mt-1">
                    {productDetailsModal.product_metadata.category ||
                      t("sources.notAvailable", "N/A")}
                  </p>
                </div>
              </div>

              {/* Price and Rating */}
              <div className="grid grid-cols-1 gap-4 rounded-[10px] bg-[#F8FAFC] p-4 md:grid-cols-3">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    {t("sources.productPrice", "Price")}
                  </label>
                  <p className="text-lg font-semibold mt-1">
                    {productDetailsModal.product_metadata.price
                      ? `${productDetailsModal.product_metadata.price} ${
                          productDetailsModal.product_metadata.currency || "EUR"
                        }`
                      : t("sources.notAvailable", "N/A")}
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    {t("sources.productAvailability", "Availability")}
                  </label>
                  <p className="text-sm mt-1">
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        productDetailsModal.product_metadata.availability ===
                        "in_stock"
                          ? "bg-green-100 text-green-800"
                          : productDetailsModal.product_metadata
                                .availability === "out_of_stock"
                            ? "bg-red-100 text-red-800"
                            : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {productDetailsModal.product_metadata.availability ||
                        t("sources.notAvailable", "N/A")}
                    </span>
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    {t("sources.productRating", "Rating")}
                  </label>
                  <div className="flex items-center mt-1">
                    {productDetailsModal.product_metadata.rating ? (
                      <>
                        <div className="flex items-center">
                          <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                          <span className="ml-1 text-sm font-medium">
                            {productDetailsModal.product_metadata.rating}
                          </span>
                        </div>
                        {productDetailsModal.product_metadata.review_count && (
                          <span className="ml-2 text-xs text-muted-foreground">
                            {t("sources.reviewsCount", "({{count}} reviews)", {
                              count:
                                productDetailsModal.product_metadata
                                  .review_count,
                            })}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="text-sm text-muted-foreground">
                        {t("sources.notAvailable", "N/A")}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Description */}
              {(productDetailsModal.product_description ||
                productDetailsModal.product_metadata?.description) && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    {t("sources.productDescription", "Description")}
                  </label>
                  <p className="text-sm mt-1 text-gray-700 leading-relaxed">
                    {productDetailsModal.product_description ||
                      productDetailsModal.product_metadata?.description}
                  </p>
                </div>
              )}

              {/* Tags */}
              {productDetailsModal.product_metadata.tags &&
                productDetailsModal.product_metadata.tags.length > 0 && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      {t("sources.tags")}
                    </label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {productDetailsModal.product_metadata.tags.map(
                        (tag, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center rounded-full bg-[#F1F5F9] px-2 py-0.5 text-xs font-medium text-[#334155]"
                          >
                            {tag}
                          </span>
                        ),
                      )}
                    </div>
                  </div>
                )}

              {/* Source URL */}
              <div className="border-t pt-4">
                <label className="text-sm font-medium text-muted-foreground">
                  {t("sources.sourceUrl")}
                </label>
                <div className="mt-1">
                  <a
                    href={productDetailsModal.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="break-all text-sm font-medium text-[#18181B] underline underline-offset-2"
                  >
                    {productDetailsModal.url}
                  </a>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <button type="button" className={GRIGIO} onClick={() => setProductDetailsModal(null)}>
              {t("common.close")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sitemap Details Modal */}
      <SitemapDetailsDialog
        isOpen={sitemapDetailsModal !== null}
        onClose={() => setSitemapDetailsModal(null)}
        source={sitemapDetailsModal}
        botId={botId}
        onDeleteAll={handleDeleteSitemapUrls}
        onReprocessSource={handleReprocessSource}
        onDeleteSource={openDeleteDialog}
        onViewProduct={setProductDetailsModal}
        processingStatuses={processingStatuses}
      />
    </div>
  );
};

export default SourcesTab;
